/**
 * EOL Runtimes container: shows Reached EOL / Approaching EOL lambdas
 * grouped by Runtime Version → Pipeline with selection checkboxes.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React, { useMemo, startTransition } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  Container,
  ExpandableSection,
  Header,
  Icon,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table
} from '@amzn/awsui-components-core';
import { Account, LambdaFunction, Pipeline } from '../types';
import { DebouncedSearchFilter } from '../components/DebouncedSearchFilter';
import { groupLambdasByStack } from '../utils/jobDisplayUtils';
import styles from '../styles.module.scss';

interface EOLRuntimeGroup {
  runtime: string;
  pipelines: Array<{ name: string; lambdas: LambdaFunction[] }>;
  totalCount: number;
}

interface EOLRuntimesContainerProps {
  // Data
  accounts: Account[];
  statsData: { eolCount: number };
  precomputedEolData: {
    eolGroups: EOLRuntimeGroup[];
    approachingGroups: EOLRuntimeGroup[];
    approachingCount: number;
  };
  // Filter state
  eolFilterType: 'reached' | 'approaching';
  setEolFilterType: (type: 'reached' | 'approaching') => void;
  searchQuery: string;
  handleSearchFilterChange: (text: string) => void;
  isViewTransitioning: boolean;
  setIsViewTransitioning: (v: boolean) => void;
  // Selection
  selectedLambdaIds: Set<string>;
  selectedLambdaIdsRef: React.MutableRefObject<Set<string>>;
  setSelectedLambdaIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  togglePipelineSelection: (pipeline: Pipeline, e: React.MouseEvent) => void;
  isPipelineSelected: (pipeline: Pipeline) => boolean;
  isPipelinePartiallySelected: (pipeline: Pipeline) => boolean;
  // Column definitions
  lambdaColumnDefinitions: any[];
  lambdaColumnDefinitionsWithSelect: any[];
}

export const EOLRuntimesContainer: React.FC<EOLRuntimesContainerProps> = ({
  accounts,
  statsData,
  precomputedEolData,
  eolFilterType,
  setEolFilterType,
  searchQuery,
  handleSearchFilterChange,
  isViewTransitioning,
  setIsViewTransitioning,
  selectedLambdaIds,
  selectedLambdaIdsRef,
  setSelectedLambdaIds,
  togglePipelineSelection,
  isPipelineSelected,
  isPipelinePartiallySelected,
  lambdaColumnDefinitions,
  lambdaColumnDefinitionsWithSelect
}) => {
  const runtimeGroups = eolFilterType === 'reached' ? precomputedEolData.eolGroups : precomputedEolData.approachingGroups;

  const filteredGroups = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return runtimeGroups;
    return runtimeGroups
      .map((group) => ({
        ...group,
        pipelines: group.pipelines
          .map((p) => ({
            ...p,
            lambdas: p.lambdas.filter(
              (l) =>
                l.name.toLowerCase().includes(query) ||
                l.arn.toLowerCase().includes(query) ||
                l.runtime.toLowerCase().includes(query) ||
                p.name.toLowerCase().includes(query)
            )
          }))
          .filter((p) => p.lambdas.length > 0)
      }))
      .map((group) => ({
        ...group,
        totalCount: group.pipelines.reduce((sum, p) => sum + p.lambdas.length, 0)
      }))
      .filter((group) => group.pipelines.length > 0);
  }, [runtimeGroups, searchQuery]);

  /** Find a representative pipeline from accounts for selection logic. */
  const findRepresentativePipeline = (pipelineName: string): Pipeline | undefined => {
    for (const account of accounts) {
      const found = account.pipelines.find((p) => p.name === pipelineName);
      if (found) return found;
    }
    return undefined;
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              <DebouncedSearchFilter onFilterChange={handleSearchFilterChange} placeholder="Filter EOL resources" />
              <SegmentedControl
                selectedId={eolFilterType}
                onChange={({ detail }) => {
                  setIsViewTransitioning(true);
                  setEolFilterType(detail.selectedId as 'reached' | 'approaching');
                  setTimeout(() => setIsViewTransitioning(false), 100);
                }}
                options={[
                  { id: 'reached', text: `Reached EOL (${statsData.eolCount})` },
                  { id: 'approaching', text: `Approaching EOL (${precomputedEolData.approachingCount})` }
                ]}
              />
            </SpaceBetween>
          }
        >
          EOL Runtimes
        </Header>
      }
    >
      {isViewTransitioning ? (
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="loading">Loading...</StatusIndicator>
        </Box>
      ) : filteredGroups.length === 0 ? (
        <Box textAlign="center" padding="xxl" color="text-body-secondary">
          {eolFilterType === 'reached' ? 'No lambdas with EOL runtimes found' : 'No lambdas with approaching EOL runtimes found'}
        </Box>
      ) : (
        <SpaceBetween size="m">
          {filteredGroups.map(({ runtime, pipelines, totalCount }) => (
            <ExpandableSection
              key={runtime}
              variant="container"
              defaultExpanded={false}
              header={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  {eolFilterType === 'reached' ? (
                    <Box fontWeight="bold" color="text-status-warning">
                      ⚠️ {runtime}
                    </Box>
                  ) : (
                    <Box fontWeight="bold">{runtime}</Box>
                  )}
                  <Badge color={eolFilterType === 'reached' ? 'red' : 'grey'}>{totalCount} functions</Badge>
                  <Badge color="grey">{pipelines.length} pipelines</Badge>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="s">
                {pipelines.map(({ name, lambdas }) => {
                  const representativePipeline = findRepresentativePipeline(name);

                  if (name === 'Manual Deployed Stacks') {
                    return (
                      <ManualDeployedStacksEOLSection
                        key={`${runtime}-${name}`}
                        runtime={runtime}
                        lambdas={lambdas}
                        representativePipeline={representativePipeline}
                        selectedLambdaIdsRef={selectedLambdaIdsRef}
                        setSelectedLambdaIds={setSelectedLambdaIds}
                        togglePipelineSelection={togglePipelineSelection}
                        isPipelineSelected={isPipelineSelected}
                        isPipelinePartiallySelected={isPipelinePartiallySelected}
                        lambdaColumnDefinitions={lambdaColumnDefinitions}
                      />
                    );
                  }

                  if (name === 'Manually created') {
                    return (
                      <ExpandableSection
                        key={`${runtime}-${name}`}
                        variant="default"
                        defaultExpanded={false}
                        header={
                          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            {representativePipeline && (
                              <div
                                onClick={(e) => togglePipelineSelection(representativePipeline, e)}
                                style={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Checkbox
                                  checked={isPipelineSelected(representativePipeline)}
                                  indeterminate={isPipelinePartiallySelected(representativePipeline)}
                                  onChange={() => {}}
                                />
                              </div>
                            )}
                            <Icon name="share" variant="link" />
                            <span>{name}</span>
                            <Badge color="grey">{lambdas.length} lambdas</Badge>
                          </SpaceBetween>
                        }
                      >
                        <Table items={lambdas} columnDefinitions={lambdaColumnDefinitionsWithSelect} variant="embedded" stripedRows />
                      </ExpandableSection>
                    );
                  }

                  return (
                    <ExpandableSection
                      key={`${runtime}-${name}`}
                      variant="default"
                      defaultExpanded={false}
                      header={
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                          {representativePipeline && (
                            <div
                              onClick={(e) => togglePipelineSelection(representativePipeline, e)}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Checkbox
                                checked={isPipelineSelected(representativePipeline)}
                                indeterminate={isPipelinePartiallySelected(representativePipeline)}
                                onChange={() => {}}
                              />
                            </div>
                          )}
                          <Icon name="share" variant="link" />
                          <span>{name}</span>
                          <Badge color="blue">{lambdas.length} lambdas</Badge>
                        </SpaceBetween>
                      }
                    >
                      <Table items={lambdas} columnDefinitions={lambdaColumnDefinitions} variant="embedded" stripedRows />
                    </ExpandableSection>
                  );
                })}
              </SpaceBetween>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      )}
    </Container>
  );
};

/** Sub-component for Manual Deployed Stacks in EOL view with stack sub-grouping. */
const ManualDeployedStacksEOLSection: React.FC<{
  runtime: string;
  lambdas: LambdaFunction[];
  representativePipeline?: Pipeline;
  selectedLambdaIdsRef: React.MutableRefObject<Set<string>>;
  setSelectedLambdaIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  togglePipelineSelection: (pipeline: Pipeline, e: React.MouseEvent) => void;
  isPipelineSelected: (pipeline: Pipeline) => boolean;
  isPipelinePartiallySelected: (pipeline: Pipeline) => boolean;
  lambdaColumnDefinitions: any[];
}> = ({
  runtime,
  lambdas,
  representativePipeline,
  selectedLambdaIdsRef,
  setSelectedLambdaIds,
  togglePipelineSelection,
  isPipelineSelected,
  isPipelinePartiallySelected,
  lambdaColumnDefinitions
}) => {
  const stackGroups = groupLambdasByStack(lambdas);

  return (
    <ExpandableSection
      key={`${runtime}-Manual Deployed Stacks`}
      variant="default"
      defaultExpanded={false}
      header={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {representativePipeline && (
            <div onClick={(e) => togglePipelineSelection(representativePipeline, e)} style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={isPipelineSelected(representativePipeline)}
                indeterminate={isPipelinePartiallySelected(representativePipeline)}
                onChange={() => {}}
              />
            </div>
          )}
          <Icon name="folder" variant="subtle" />
          <span style={{ fontWeight: 600 }}>Manual Deployed Stacks</span>
          <Badge color="grey">{stackGroups.length} stacks</Badge>
          <Badge color="grey">{lambdas.length} lambdas</Badge>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="s">
        {stackGroups.map(([stackName, stackLambdas]) => {
          const stackLambdaIds = stackLambdas.map((l) => l.id);
          const allStackSelected = stackLambdaIds.every((id) => selectedLambdaIdsRef.current.has(id));
          const someStackSelected = stackLambdaIds.some((id) => selectedLambdaIdsRef.current.has(id));

          const toggleStackSelection = (e: React.MouseEvent) => {
            e.stopPropagation();
            startTransition(() => {
              setSelectedLambdaIds((prev) => {
                const next = new Set(prev);
                if (allStackSelected) {
                  stackLambdaIds.forEach((id) => next.delete(id));
                } else {
                  stackLambdaIds.forEach((id) => next.add(id));
                }
                return next;
              });
            });
          };

          return (
            <ExpandableSection
              key={`${runtime}-Manual Deployed Stacks-${stackName}`}
              variant="default"
              defaultExpanded={false}
              header={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <div onClick={toggleStackSelection} style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={allStackSelected} indeterminate={someStackSelected && !allStackSelected} onChange={() => {}} />
                  </div>
                  <Icon name="status-positive" variant="subtle" />
                  <span style={{ fontWeight: 500 }}>{stackName}</span>
                  <Badge color="blue">{stackLambdas.length} lambdas</Badge>
                </SpaceBetween>
              }
            >
              <Table items={stackLambdas} columnDefinitions={lambdaColumnDefinitions} variant="embedded" stripedRows />
            </ExpandableSection>
          );
        })}
      </SpaceBetween>
    </ExpandableSection>
  );
};
